import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Extender } from '@firsttable/web-components/atoms';
import { decodeEntities } from '@firsttable/functions';
import { motion, AnimatePresence } from 'motion/react';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import parseHtml from '@/lib/parseHtml';

const MotionBox = motion(Box);

const Content = ({
  content = '',
  lines = 12,
  lineHeight = 26,
  children,
  defaultExpandedWhenOverflowing = false,
}) => {
  const { track } = useAnalytics();
  const [isClipped, setIsClipped] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  const maxHeight = lines * lineHeight;

  useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current;
      const contentOverflows = element.scrollHeight > maxHeight;
      setIsOverflowing(contentOverflows);
      if (defaultExpandedWhenOverflowing && contentOverflows) {
        setIsClipped(false);
      } else {
        setIsClipped(contentOverflows);
      }
    }
  }, [content, maxHeight, defaultExpandedWhenOverflowing]);

  const toggleInfo = () => {
    track('button_clicked', {
      button_name: 'expand overview info',
      button_type: 'alter component',
      section: 'overview',
    });
    setIsClipped(!isClipped);
  };

  const renderParagraphs = useMemo(() => {
    const decodeAndRender = (para, i) => {
      const text = decodeEntities(para);
      const isHtml = /<[^>]*>?/gm.test(text);
      return <Text key={i}>{isHtml ? parseHtml(text) : text}</Text>;
    };
    return content.split('\n').map(decodeAndRender);
  }, [content]);

  return (
    <>
      <MotionBox
        ref={contentRef}
        initial={{ height: isClipped ? maxHeight : 'auto' }}
        animate={{
          height: isClipped ? maxHeight : 'auto',
          transition: { duration: 0.4, ease: 'easeInOut' },
        }}
        overflow="hidden"
        css="p:last-child { margin-bottom: 0; }">
        {renderParagraphs}
        {children}
      </MotionBox>
      <AnimatePresence>
        {isOverflowing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}>
            <Extender m="8px 0 0" gradient isExpanded={!isClipped} onClick={toggleInfo} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

Content.propTypes = {
  content: PropTypes.string,
  lines: PropTypes.number,
  lineHeight: PropTypes.number,
  defaultExpandedWhenOverflowing: PropTypes.bool,
};

export default Content;
